import { TextField, FormControl, FormControlLabel, RadioGroup, Radio, Switch } from '../../ui';
import { DatePicker } from '../../ui';
import { format, parse } from 'date-fns';
import type { TFunction } from 'i18next';
import { type Question, type AnswerCreate } from '../../api/questionnaire';

/** `valueDate` is a calendar date and carries no time; both directions parse
 *  and format it as one. `new Date('yyyy-mm-dd')` reads the string as UTC
 *  midnight and `toISOString()` converts local midnight back to UTC, so the two
 *  together shift the day at any non-zero offset -- forwards for a reader east
 *  of UTC, backwards for one west of it. A date of birth is the case where that
 *  is not a rounding error. */
const DATE_ONLY = 'yyyy-MM-dd';

export function QuestionInput(props: {
  question: Question;
  currentAnswer: {
    valueText?: string;
    valueNumber?: number;
    valueDate?: string;
    valueBoolean?: boolean;
    valueOptionCode?: string;
  };
  handleAnswerChange: (question: Question, value: Partial<AnswerCreate>) => Promise<void>;
  t: TFunction;
}) {
  /**
   * Renders a single question based on its declared type.
   *
   * Input handling:
   * All inputs delegate to `handleAnswerChange`, ensuring consistent state updates and
   * auto-save behavior across question types.
   *
   * Date handling:
   * Dates are stored as ISO yyyy-mm-dd strings rather than full timestamps to avoid timezone
   * ambiguity and to align with typical backend date fields.
   */
  const { question, handleAnswerChange, t } = props;
  const currentAnswer = props.currentAnswer || {};

  switch (question.type) {
    case 'Text':
      return (
        <TextField
          fullWidth
          multiline={!question.isIdentityField}
          rows={question.isIdentityField ? 1 : 3}
          value={currentAnswer.valueText || ''}
          onChange={(e) => handleAnswerChange(question, { valueText: e.target.value })}
          placeholder={
            question.isIdentityField && question.identityFieldType
              ? t(`publicQuestionnairePage.placeholders.${question.identityFieldType.replace('_', '')}`)
              : t('publicQuestionnairePage.placeholders.text')
          }
          type={question.identityFieldType === 'email' ? 'email' : 'text'}
        />
      );

    case 'Number':
      return (
        <TextField
          fullWidth
          type="number"
          value={currentAnswer.valueNumber ?? ''}
          onChange={(e) =>
            handleAnswerChange(question, {
              /**
               * Empty input is mapped to undefined so that drafts do not persist spurious NaN values.
               * Parsing uses parseFloat, reflecting the numeric semantics expected by the backend.
               */
              valueNumber: e.target.value ? parseFloat(e.target.value) : undefined,
            })
          }
          placeholder={t('publicQuestionnairePage.placeholders.number')}
        />
      );

    case 'Date':
      return (
        /* No provider of its own: PickerLocaleProvider at the root supplies the
           adapter and the region's locale, and a local one would override it
           with the adapter's English default. */
        <DatePicker
          value={
            currentAnswer.valueDate
              ? parse(currentAnswer.valueDate, DATE_ONLY, new Date())
              : null
          }
          onChange={(date) =>
            handleAnswerChange(question, {
              valueDate: date ? format(date, DATE_ONLY) : undefined,
            })
          }
          slotProps={{
            textField: { fullWidth: true },
          }}
        />
      );

    case 'List':
      return (
        <FormControl as="fieldset" fullWidth>
          <RadioGroup
            value={currentAnswer.valueOptionCode || ''}
            onChange={(e) => handleAnswerChange(question, { valueOptionCode: e.target.value })}
          >
            {question.listOptions.map((option) => (
              <FormControlLabel key={option.id} value={option.code} control={<Radio />} label={option.value} />
            ))}
          </RadioGroup>
        </FormControl>
      );

    case 'Boolean':
      return (
        <FormControlLabel
          control={
            <Switch
              checked={currentAnswer.valueBoolean || false}
              onChange={(e) => handleAnswerChange(question, { valueBoolean: e.target.checked })}
            />
          }
          /**
           * The label is currently literal. If consistent localization is required, these values
           * should be sourced from i18n keys to match the remainder of the page.
           */
          label={currentAnswer.valueBoolean ? 'Yes' : 'No'}
        />
      );

    default:
      /**
       * Unknown question types are ignored to prevent runtime failures. This favors resilience
       * in the presence of backend schema evolution, while relying on server-side validation
       * to detect unsupported configurations.
       */
      return null;
  }
}
